import { Banknote, FileTextIcon } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { capitalizeWithSpaces, ScrollToElement } from "utils";
import type { BidPostData } from "validations";

import { OnboardingTask } from "@krea/lender-features/components/onboarding-task";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@krea/shared/design-system/accordion";
import { Badge } from "@krea/shared/design-system/Badge";
import { BidBenefits } from "@krea/shared/design-system/BidBenefits";
import { Button } from "@krea/shared/design-system/Button";
import { EuFlag } from "@krea/shared/design-system/icons/EuFlag";
import { Spinner } from "@krea/shared/design-system/Spinner";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import { PromotionTypeEnum } from "@krea/shared/types/common";
import { cn } from "@krea/shared/utils";
import {
  hasEuLoanPromotion,
  withoutEuLoanPromotion,
} from "@krea/shared/utils/hasEuLoanPromotion";

import {
  LenderApplicationTypeEnum,
  type OnboardingStepEnum,
} from "@/features/common/enums";
import { useGetCurrentLenderCompanyQuery } from "@/features/lender-companies/hooks/use-get-lender-company.query";
import { AttractivenessScoreBadge } from "@/features/lender-responses/components/offer-attractiveness";
import { useChangeOnboardingStatus } from "@/features/lender-responses/hooks/data/use-change-onboarding-status.mutation";
import type {
  IPromotion,
  IPromotionInput,
} from "@/features/lender-responses/types/lender-response";

import { useLenderApplicationDetailsStore } from "../lenderApplicationDetailsStore";
import { BidDetails, BidSummary } from "./BidDetails";
import {
  bidFramerVariants,
  getFormValuesFromLenderResponse,
  type IBidProps,
  useLenderResponseCommonProperties,
} from "./bidHelpers";
import LenderResponseFieldRequestDetails from "./field-requests/LenderResponseFieldRequestDetails";

interface ILenderResponseProps {
  lenderResponse: IBidProps["lenderResponse"];
  preview: IBidProps["preview"];
  lenderApplication: IBidProps["lenderApplication"];
  isPreviewLoading?: boolean;
}

export const LenderResponse = ({
  lenderResponse,
  preview,
  lenderApplication,
  isPreviewLoading,
}: ILenderResponseProps) => {
  const { data: lenderCompany } = useGetCurrentLenderCompanyQuery();
  const { type, loanApplication } = lenderApplication;
  // Benefits belong to the response, not a bid: a combo offer shows one list.
  const benefits = lenderResponse.benefits ?? [];
  const { currency } = loanApplication;
  const { reset } = useFormContext<BidPostData>();
  const setCurrentlyUpdatingLenderResponseId = useLenderApplicationDetailsStore(
    (state) => state.setCurrentlyUpdatingLenderResponseId,
  );

  const {
    mutate: updateOnboardingSteps,
    isPending: isLoadingUpdateOnboarding,
  } = useChangeOnboardingStatus();

  const onChangeBoardingFields = useCallback(
    (checked: boolean, field: OnboardingStepEnum) => {
      if (!("id" in lenderResponse)) return;

      updateOnboardingSteps({
        data: {
          steps: [{ type: field, value: checked }],
        },
        lenderCompanyId: Number(lenderCompany?.id),
        lenderResponseId: lenderResponse.id,
      });
    },
    [lenderResponse, lenderCompany?.id, updateOnboardingSteps],
  );

  const onUpdate = useCallback(() => {
    if (!("id" in lenderResponse)) return;

    setCurrentlyUpdatingLenderResponseId(lenderResponse.id);

    const values = getFormValuesFromLenderResponse(
      lenderResponse,
      lenderCompany?.isFeatureInterestRatePerYearActivated,
    );

    reset(values);
  }, [
    lenderResponse,
    reset,
    setCurrentlyUpdatingLenderResponseId,
    lenderCompany?.isFeatureInterestRatePerYearActivated,
  ]);

  const {
    additionalInformationFieldRequests,
    collateralFieldRequests,
    dateCreated,
    dateUpdated,
    isOpen,
    isUpdated,
    onDelete,
    readMoreText,
    showMore,
    statusText,
    statusVariant,
    isSelectedLenderResponse,
  } = useLenderResponseCommonProperties({
    lenderResponse,
    lenderApplication,
    preview,
  });

  const getPromotionAsText = useCallback(
    (promotion: IPromotion | IPromotionInput): string => {
      // EU_LOAN is a flag with no month count, so it must bypass the value
      // guard below — otherwise it renders as an empty string.
      if (promotion.type === PromotionTypeEnum.EU_LOAN)
        return "Loan with EU support";

      if (!promotion.value) return ""; // Should never happen, but just in case

      const prefixText =
        promotion.value === 1
          ? "First month"
          : `First ${promotion.value} months`;

      switch (promotion.type) {
        case PromotionTypeEnum.INTEREST_FREE:
          return `${prefixText} without interest`;
        case PromotionTypeEnum.AMORTISATION_FREE:
          return `${prefixText} without amortization`;
      }
    },
    [],
  );

  const isEuLoan = hasEuLoanPromotion(lenderResponse.promotions);
  // EU_LOAN names a kind of loan, not a limited-time offer, so it is labelled on
  // its own; only the rest take the "Special offer:" prefix.
  const otherPromotionTexts = withoutEuLoanPromotion(lenderResponse.promotions)
    .map(getPromotionAsText)
    .filter(Boolean);

  return (
    <AnimatePresence>
      <motion.div
        key={
          "id" in lenderResponse
            ? `${lenderResponse.bids[0]?.type}-${lenderResponse.id}`
            : `new`
        }
        initial="initial"
        exit="hidden"
        animate="visible"
        variants={bidFramerVariants}
        className={cn("rounded-xl border border-gray-200 bg-white p-4", {
          "border-primary": isSelectedLenderResponse,
        })}
      >
        <div
          className={cn({
            "pt-2": preview,
          })}
        >
          {!preview && statusVariant && (
            <div className="flex flex-col flex-wrap justify-between gap-2 text-sm lg:flex-row ">
              <div className="flex flex-col flex-wrap items-start gap-2 text-sm lg:flex-row ">
                <Badge
                  className="items-center uppercase"
                  variant={statusVariant}
                >
                  {statusText}
                </Badge>
                <div className="flex flex-row items-start gap-1">
                  <div>Created:</div>
                  <span className="text-gray-600">{dateCreated}</span>
                </div>
                {isUpdated && (
                  <div className="flex flex-row items-start gap-1">
                    <div>Updated:</div>
                    <span className="text-gray-600">{dateUpdated}</span>
                  </div>
                )}
              </div>
              <div className="flex flex-col flex-wrap items-start gap-2 text-sm lg:flex-row ">
                {"attractivenessScore" in lenderResponse && (
                  <AttractivenessScoreBadge
                    score={lenderResponse.attractivenessScore}
                  />
                )}
                {"creditTermsFile" in lenderResponse &&
                  lenderResponse.creditTermsFile?.fileUrl && (
                    <Button
                      variant="ghost"
                      className="my-1 text-primary"
                      onClick={() => {
                        if (lenderResponse.creditTermsFile?.fileUrl) {
                          window.open(
                            lenderResponse.creditTermsFile.fileUrl,
                            "_blank",
                          );
                        }
                      }}
                    >
                      <div className="flex items-center gap-2 text-sm">
                        <FileTextIcon className="size-5 cursor-pointer" />
                        <span>Termsheet</span>
                      </div>
                    </Button>
                  )}
              </div>
            </div>
          )}

          <div className="mt-2 w-full rounded-md border border-l-8 border-solid border-gray-300 border-l-primary  p-3">
            {preview && lenderResponse.bids.length === 0 ? (
              <div className="rounded-sm bg-white p-6 text-center">
                {isPreviewLoading ? (
                  <Spinner
                    className="my-2 text-primary"
                    iconClassName="size-10"
                  />
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <div className="flex flex-col items-center gap-1 rounded-lg bg-white p-4">
                      <div className="flex size-8 items-center justify-center rounded-full  text-primary">
                        <Banknote className="size-8" strokeWidth={1.5} />
                      </div>
                      <p className="text-base-strong m-0">
                        No offer preview yet
                      </p>
                      <p className="text-base m-0 leading-relaxed">
                        Complete the form with valid values to see your offer
                        preview here.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              lenderResponse.bids.map((bid, index) => (
                <div key={`${index}-${bid.type}`}>
                  <div className="flex w-full flex-col items-stretch gap-x-8 gap-y-3 py-3 pl-2 xl:flex-row xl:flex-wrap xl:items-start">
                    <p className="text-base-strong m-0 shrink-0 capitalize">
                      {capitalizeWithSpaces(bid.type)}
                    </p>
                    <div className="w-full ">
                      <BidSummary
                        key={`${index}-${bid.type}`}
                        bid={bid}
                        currency={currency}
                      />
                    </div>
                  </div>
                  {index < lenderResponse.bids.length - 1 && (
                    <hr className="my-0 border-solid border-gray-300" />
                  )}
                </div>
              ))
            )}

            {benefits.length > 0 && (
              <>
                <hr className="my-1 border-solid border-gray-200" />
                <div className="flex flex-row items-start gap-2 py-1 pl-2 text-sm">
                  <div className="shrink-0">Benefits:</div>
                  <BidBenefits benefits={benefits} isCondensedVersion />
                </div>
              </>
            )}
          </div>
          {isSelectedLenderResponse && (
            <OnboardingTask
              onboardingStartedAt={lenderResponse.onboardingStartedAt}
              creditAssessmentDoneAt={lenderResponse.creditAssessmentDoneAt}
              onChange={(value, type) =>
                onChangeBoardingFields(value, type as OnboardingStepEnum)
              }
              disabled={
                ("id" in lenderResponse && !!lenderResponse.paidOutAt) ||
                isLoadingUpdateOnboarding
              }
              signingLinkClickedAt={lenderResponse.signingLinkClickedAt}
            />
          )}

          <AnimatePresence initial={false}>
            {isOpen && (
              <motion.div
                initial="collapsed"
                animate="open"
                exit="collapsed"
                variants={{
                  open: {
                    opacity: 1,
                    height: "auto",
                    transition: {
                      type: "tween",
                      duration: 0.1,
                      ease: "easeOut",
                    },
                  },
                  collapsed: {
                    opacity: 0,
                    height: 0,
                    transition: {
                      type: "tween",
                      duration: 0.1,
                      ease: "easeOut",
                    },
                  },
                }}
                transition={{ duration: 0.1 }}
                className="pb-4 pt-5"
              >
                <div className="flex flex-col gap-4 lg:flex-row">
                  <div className="flex w-full flex-col gap-4 lg:w-1/2">
                    {benefits.length > 0 && (
                      <div className="rounded-xl border border-solid border-gray-200 p-3">
                        <BidBenefits benefits={benefits} title="Benefits" />
                      </div>
                    )}

                    {collateralFieldRequests.length > 0 && (
                      <div className="rounded-xl border border-solid border-gray-200 p-3">
                        <LenderResponseFieldRequestDetails
                          title="Collateral"
                          fieldRequests={collateralFieldRequests}
                          currency={currency}
                        />
                      </div>
                    )}

                    {additionalInformationFieldRequests.length > 0 && (
                      <div className="rounded-xl border border-solid border-gray-200 p-3">
                        <LenderResponseFieldRequestDetails
                          title="Information for final credit decision"
                          fieldRequests={additionalInformationFieldRequests}
                          currency={currency}
                        />
                      </div>
                    )}
                  </div>
                  <div className="mb-5 w-full rounded-xl border border-solid border-gray-200 bg-white p-4 lg:mb-0 lg:w-1/2">
                    {/* {detailsContent} */}
                    <Tabs defaultValue={`0-${lenderResponse.bids[0]?.type}`}>
                      <TabsList>
                        {lenderResponse.bids.map((bid, index) => (
                          <TabsTrigger
                            key={`${index}-${bid.type}`}
                            value={`${index}-${bid.type}`}
                          >
                            {capitalizeWithSpaces(bid.type)}
                          </TabsTrigger>
                        ))}
                      </TabsList>
                      {lenderResponse.bids.map((bid, index) => (
                        <TabsContent
                          key={`${index}-${bid.type}`}
                          value={`${index}-${bid.type}`}
                        >
                          <BidDetails
                            key={`${index}-${bid.type}-details`}
                            bid={bid}
                            currency={currency}
                          />
                        </TabsContent>
                      ))}
                    </Tabs>
                  </div>
                </div>

                {lenderCompany?.name && lenderCompany.description && (
                  <Accordion
                    type="multiple"
                    className="mt-4 flex flex-col gap-3"
                  >
                    <AccordionItem
                      key={type}
                      value={type}
                      className="relative rounded-xl border pl-3 pr-2"
                    >
                      <AccordionTrigger className="">
                        <div> {`About ${lenderCompany.name}`} </div>
                      </AccordionTrigger>

                      <AccordionContent>
                        <div
                          dangerouslySetInnerHTML={{
                            __html: lenderCompany.description,
                          }}
                        />
                        {lenderCompany.registeredAtFinansinspektionenAt && (
                          <div className="text-helper mt-3 text-center">
                            Approved by Finansinspektionen
                          </div>
                        )}
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {(isEuLoan || otherPromotionTexts.length > 0) && (
            <div
              className={cn(
                "-mx-4 -mb-3 mt-2 rounded-t p-2 text-sm",
                // Matches the customer dashboard's strip, so a lender previewing
                // the offer sees what the customer will see.
                isEuLoan ? "bg-blue-100 " : "bg-mint-100",
              )}
            >
              <div className="px-2">
                {isEuLoan && (
                  <div className="flex items-center gap-2">
                    <span className="w-[21px] shrink-0">
                      <EuFlag />
                    </span>
                    <span className="font-semibold">Loan with EU support</span>
                  </div>
                )}
                {otherPromotionTexts.length > 0 && (
                  // Its own row under the EU loan, matching the customer strip.
                  <div className={cn({ "mt-1": isEuLoan })}>
                    <span className="">Special offer: </span>
                    <span className="font-medium">
                      {otherPromotionTexts.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="-mx-4 mt-3 border-t border-gray-200 px-4">
            <div className="flex justify-end gap-4 pt-4">
              <Button variant="secondary" onClick={showMore}>
                {readMoreText}
              </Button>

              {!(
                preview ||
                !("isActive" in lenderResponse && lenderResponse.isActive) ||
                type ===
                  LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_BID_SELECTED ||
                type ===
                  LenderApplicationTypeEnum.THIS_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER ||
                type ===
                  LenderApplicationTypeEnum.OTHER_LENDER_APPLICATION_PAYOUT_RECEIVED_BY_CUSTOMER ||
                type ===
                  LenderApplicationTypeEnum.FAILED_LENDER_APPLICATION_FILTER ||
                type === LenderApplicationTypeEnum.CLOSED_BY_CUSTOMER
              ) && (
                <>
                  <Button
                    onClick={() => {
                      onUpdate();
                      ScrollToElement("scrollToBidsForm");
                    }}
                  >
                    Edit
                  </Button>
                  <Button variant="danger" onClick={onDelete}>
                    Remove
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
